import { CustomState } from "@hardware/Store/Reducers";
import setFieldValue from "@hardware/Store/State/CreateAccount/SetFieldValue";
import { CreateAccountFormContext } from "@hardware/Widgets/CreateAccount/CreateAccountForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { loadCurrentCountriesParameter } from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => {
    const fieldName = "state";

    let states: StateModel[] = [];
    if (state.createAccountForm.fieldValues["country"]) {
        const countries = getCountriesDataView(state, loadCurrentCountriesParameter).value;
        const countrySelected = countries?.find(
            country => country.name === state.createAccountForm.fieldValues["country"],
        );

        if (countrySelected) {
            states = countrySelected.states ?? [];
        }
    }

    return {
        fieldName,
        fieldValue: state.createAccountForm.fieldValues[fieldName],
        states,
    };
};

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FormFieldStyles {
    selectField?: SelectPresentationProps;
}

export const formFieldStyles: FormFieldStyles = {};

const styles = formFieldStyles;

const Company: React.FC<Props> = ({ fields, fieldName, fieldValue, setFieldValue, states }) => {
    const { label, hintText, isRequired } = fields;
    const createAccountFormContext = React.useContext(CreateAccountFormContext);
    const [fieldErrorMessage, setFieldErrorMessage] = React.useState("");

    React.useEffect(() => {
        createAccountFormContext.validators[fieldName] = validateField;
        if (typeof fieldValue !== "undefined") {
            validateField();
        }
    }, [fieldValue]);

    React.useEffect(() => {
        createAccountFormContext.validators[fieldName] = validateField;
    }, [isRequired]);

    const validateField = () => {
        const errorMessage = isRequired && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setFieldErrorMessage(errorMessage);
        return !errorMessage;
    };

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: fieldName, value: event.target.value });
    };

    return (
        <Select
            {...styles.selectField}
            label={label}
            required={isRequired}
            value={fieldValue || ""}
            error={fieldErrorMessage}
            onChange={fieldChangeHandler}
        >
            <option value="">{hintText}</option>
            {states &&
                states.map(state => (
                    <option key={state.id} value={state.name}>
                        {state.name}
                    </option>
                ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Company as any) as any,
    definition: {
        group: "Create Account" as any,
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "State",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
